import React from 'react';

import styles from './AuthPendingOverlay.module.css';

interface AuthPendingOverlayProps {
	/** Nothing renders when false. */
	isVisible: boolean;
	message?: string;
	hint?: string;
}

/**
 * Shown while a sign-in is in flight.
 *
 * Google's One Tap prompt disappears the moment it's clicked, and the work
 * that follows — exchanging the credential, then reloading — is invisible.
 * From the customer's side the page simply sits there, which reads as broken:
 * they click again, and a second attempt at an auth flow is how you get a
 * duplicate popup or a wedged sign-in.
 *
 * So this is deliberately a blocking overlay rather than an inline spinner:
 * it says something is happening AND it puts a surface between the customer
 * and the control they'd otherwise click twice.
 */
const AuthPendingOverlay: React.FC<AuthPendingOverlayProps> = ({
	isVisible,
	message = 'Signing you in…',
	hint = 'This only takes a moment.',
}) => {
	if (!isVisible) return null;

	return (
		<div
			className={styles.overlay}
			// Assistive tech should announce this the moment it appears, and the
			// busy state keeps it from being read as ordinary page content.
			role="status"
			aria-live="polite"
			aria-busy="true"
		>
			<div className={styles.spinner} aria-hidden="true" />
			<p className={styles.message}>{message}</p>
			{hint && <p className={styles.hint}>{hint}</p>}
		</div>
	);
};

export default AuthPendingOverlay;
